import React, { forwardRef, useState } from "react"
import Icon from "../Icons/Icon/Icon"
import { TIconKey } from "../Icons/Icon/Interfaces"
import PlainButton from "components/ProjectNonSpecificComponents/Elements/PlainButton"
import concatWithSpaces from "components/ProjectNonSpecificComponents/Functions/concatWithSpaces"

interface Props extends React.ComponentProps<"button"> {
	iconKey: TIconKey
	iconWidth?: string | number
	iconHeight?: string | number
	iconColor?: string
	backgroundColorOnHover?: string
	iconColorOnHover?: string
	/**
	 * Disables the invisible `<div />` that hovers above icon,
	 * in order to increase the range in which the button is clickable/tappable.
	 *
	 * It is best to disable when two buttons are being
	 * put together next to each other to avoid overlap.
	 */
	disableUXPadding?: boolean
}

const IconButton = forwardRef<HTMLButtonElement, Props>(
	(
		{
			iconKey,
			iconWidth,
			iconHeight,
			iconColor,
			backgroundColorOnHover,
			iconColorOnHover,
			disabled = false,
			disableUXPadding = false,
			onClick,
			onMouseEnter = () => {},
			onMouseLeave = () => {},
			style,
			className,
			...props
		},
		ref
	) => {
		const [hovered, setHovered] = useState<boolean>(false)
		const hasPointer = !disabled && onClick

		return (
			<PlainButton
				{...props}
				className={concatWithSpaces(
					"relative center-x-y",
					hasPointer ? "pointer" : "",
					disabled ? "not-al12lowed" : "",
					className
				)}
				style={{
					backgroundColor: hovered ? backgroundColorOnHover : undefined,
					borderRadius: backgroundColorOnHover ? 5 : 0,
					// Animation
					transition: "0.2s var(--default-slide-transition-fn)",
					transitionProperty: "background-color",
					...style,
				}}
				disabled={disabled}
				onClick={onClick}
				onMouseEnter={(e) => {
					setHovered(true)
					onMouseEnter(e)
				}}
				onMouseLeave={(e) => {
					setHovered(false)
					onMouseLeave(e)
				}}
				ref={ref}
			>
				{!disableUXPadding && (
					<div
						style={{
							position: "absolute",
							width: "168%",
							height: "168%",
							zIndex: 1,
						}}
					/>
				)}
				<Icon
					iconKey={iconKey}
					width={iconWidth}
					height={iconHeight}
					iconColor={hovered ? iconColorOnHover : iconColor}
				/>
			</PlainButton>
		)
	}
)

export default IconButton
